export type NormalizedSyntheticBondApiError = {
  message: string;
  code?: string;
  fields?: Record<string, string>;
};

const flattenMessages = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(
      flattenMessages,
    );
  }
  return [String(value)];
};

export const normalizeSyntheticBondApiError = (
  error: unknown,
  fallback = 'The request could not be completed.',
): NormalizedSyntheticBondApiError => {
  if (error instanceof Error) {
    return { message: error.message || fallback };
  }
  if (typeof error === 'string') return { message: error || fallback };
  if (!error || typeof error !== 'object') return { message: fallback };

  const object = error as Record<string, unknown>;
  const code = typeof object.code === 'string' ? object.code : undefined;
  const preferred = flattenMessages(object.detail || object.message);
  const errors = flattenMessages(object.errors);
  const fields = Object.entries(object).reduce(
    (result, [key, value]) => {
      if (['code', 'detail', 'message', 'errors', 'context'].includes(key)) {
        return result;
      }
      const message = flattenMessages(value).join(' ');
      if (message) result[key] = message;
      return result;
    },
    {} as Record<string, string>,
  );
  const fieldMessages = Object.entries(fields).map(
    ([field, message]) => `${field}: ${message}`,
  );

  return {
    code,
    fields: Object.keys(fields).length ? fields : undefined,
    message:
      [...preferred, ...errors, ...fieldMessages].join(' ') || fallback,
  };
};
